import React from "react";
import {
  Table, TableHead, TableBody, TableRow, TableCell, Chip,
  Box, Stack, Typography, Link as MuiLink, useTheme, useMediaQuery,
} from "@mui/material";
import { Link } from "react-router-dom";

const statusChip = (status) => {
  const raw = typeof status === "string" ? status.toLowerCase() : status;
  const isActive = raw === "active" || raw === 1 || raw === "1" || raw === 2 || raw === "2";
  return (
    <Chip
      size="small"
      label={isActive ? "Active" : "Inactive"}
      sx={{
        bgcolor: isActive ? "#dcfce7" : "#e5e7eb",
        color: isActive ? "#166534" : "#374151",
        fontWeight: 600,
      }}
    />
  );
};

const courseLink = (row) => (
  <MuiLink
    component={Link}
    to={`/admin/course/${row.id}`}
    underline="hover"
    sx={{ color: "#7c3aed", fontWeight: 600 }}
  >
    {row.name}
  </MuiLink>
);

export default function RecentCoursesTable({ rows }) {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const data = rows || [];

  const header = (
    <Stack direction="row" justifyContent="space-between" alignItems="center" mb={1}>
      <Typography variant="subtitle1" fontWeight={700} sx={{ color: "#111827" }}>Recent Courses</Typography>
      <MuiLink
        component={Link}
        to="/admin/course-list"
        underline="hover"
        sx={{ color: "#7c3aed", fontSize: 13, fontWeight: 600 }}
      >
        View all
      </MuiLink>
    </Stack>
  );

  if (isMobile) {
    return (
      <Box>
        {header}
        <Stack spacing={1}>
          {data.map((r) => (
            <Box key={r.id} sx={{ p: 1.5, border: "1px solid #e5e7eb", borderRadius: 1 }}>
              <Box>{courseLink(r)}</Box>
              <Typography variant="caption" sx={{ color: "#374151", fontWeight: 700 }}>
                {r.academic_year ? `${r.academic_year} · ` : r.year ? `Year ${r.year} · ` : ""}
                {r.start_date || "—"} → {r.end_date || "—"} · {r.groups_count ?? r.plans_count ?? 0} groups
              </Typography>
              <Box mt={0.5}>{statusChip(r.status)}</Box>
            </Box>
          ))}
          {data.length === 0 && <Typography variant="body2" sx={{ color: "#374151" }}>No data</Typography>}
        </Stack>
      </Box>
    );
  }

  return (
    <Box>
      {header}
      <Table size="small">
        <TableHead>
          <TableRow>
            <TableCell sx={{ fontWeight: 800, color: "#111827" }}>Course Name</TableCell>
            <TableCell sx={{ fontWeight: 800, color: "#111827" }}>Academic Year</TableCell>
            <TableCell sx={{ fontWeight: 800, color: "#111827" }}>Start Date</TableCell>
            <TableCell sx={{ fontWeight: 800, color: "#111827" }}>End Date</TableCell>
            <TableCell align="right" sx={{ fontWeight: 700, color: "#64748b" }}>Groups</TableCell>
            <TableCell sx={{ fontWeight: 800, color: "#111827" }}>Status</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {data.map((r) => (
            <TableRow key={r.id} hover>
              <TableCell>{courseLink(r)}</TableCell>
              <TableCell sx={{ fontWeight: 700, color: "#111827" }}>{r.academic_year ?? (r.year ? String(r.year) : "—")}</TableCell>
              <TableCell sx={{ fontWeight: 700, color: "#111827" }}>{r.start_date || "—"}</TableCell>
              <TableCell sx={{ fontWeight: 700, color: "#111827" }}>{r.end_date || "—"}</TableCell>
              <TableCell align="right" sx={{ fontWeight: 700, color: "#111827" }}>{r.groups_count ?? r.plans_count ?? 0}</TableCell>
              <TableCell>{statusChip(r.status)}</TableCell>
            </TableRow>
          ))}
          {data.length === 0 && (
            <TableRow><TableCell colSpan={6} align="center">No data</TableCell></TableRow>
          )}
        </TableBody>
      </Table>
    </Box>
  );
}
